import { FieldValues } from "react-hook-form";

type AccountLocationFormItem = { id?: string; name?: string };
type InitialAccountLocation = { id: string; name?: string };

function splitAccountLocations(
  accountLocations: AccountLocationFormItem[] | undefined,
  initialAccountLocations: InitialAccountLocation[] | undefined
) {
  const locations = accountLocations ?? [];
  const initialById = new Map(
    (initialAccountLocations ?? []).map((l) => [l.id, (l.name ?? "").trim()])
  );

  const accountLocationsToCreate = locations
    .filter((loc) => !loc?.id)
    .map((loc) => ({ name: (loc?.name ?? "").trim() }))
    .filter((loc) => loc.name.length > 0);

  const accountLocationsToUpdate = locations
    .filter(
      (loc): loc is { id: string; name?: string } =>
        !!loc?.id && (loc?.name ?? "").trim().length > 0
    )
    .map((loc) => ({ id: loc.id, name: (loc.name ?? "").trim() }))
    .filter((loc) => initialById.get(loc.id) !== loc.name);

  const currentIds = new Set(
    locations.filter((loc): loc is { id: string } => !!loc?.id).map((l) => l.id)
  );
  const accountLocationsToDelete = (initialAccountLocations ?? [])
    .map((l) => l.id)
    .filter((id) => !currentIds.has(id));
  return {
    accountLocationsToCreate,
    accountLocationsToUpdate,
    accountLocationsToDelete,
  };
}

export const mapFormToRequestBody = async (
  props: FieldValues,
  options?: { initialAccountLocations?: InitialAccountLocation[] }
) => {
  const {
    accountLocationsToCreate,
    accountLocationsToUpdate,
    accountLocationsToDelete,
  } = splitAccountLocations(
    props?.accountLocations as AccountLocationFormItem[] | undefined,
    options?.initialAccountLocations
  );
  return {
    name: props.hospitalName,
    address: `${props?.addressLine1?.length ? props?.addressLine1 : ""}${
      props?.addressLine2?.length ? props?.addressLine2 : ""
    }`,
    city: props.city,
    state: props.state,
    zipcode: props.zipcode,
    rcm: props.rcm,
    specialty: props.specialty,
    contactName: props.contactName,
    reportType: props.reportType,
    unitHospitalNumber: props.unitHospitalNumber,
    contactPhone: props.contactPhone,
    orderType: props.orderType,
    taxId: props.taxId,
    emailToReceiveReports: props.emailToReceiveReports,
    connectAccountId: props.connectAccountId,
    messageSender: props.messageSender,
    emailSender: props.emailSender,
    emailToReceiveNotification: props.emailToReceiveNotification,
    frontDeskEmployeesToReceiveDailyEmail:
      props.frontDeskEmployeesToReceiveDailyEmail,
    patientChatBotId:
      props.patientChatBotId === "disabled"
        ? null
        : props.patientChatBotId || null,
    contractManagementChatBotId:
      props.contractManagementChatBotId === "disabled"
        ? null
        : props.contractManagementChatBotId || null,
    emailTemplateAttempt1: props.emailTemplateAttempt1,
    attempt1: props.attempt1,
    emailTemplateAttempt2: props.emailTemplateAttempt2,
    attempt2: props.attempt2,
    emailTemplateAttempt3: props.emailTemplateAttempt3,
    attempt3: props.attempt3,
    bundleEmailTemplateAttempt1: props.emailTemplateAttemptBundle1,
    bundleAttempt1: props.attemptBundle1,
    bundleEmailTemplateAttempt2: props.emailTemplateAttemptBundle2,
    bundleAttempt2: props.attemptBundle2,
    bundleEmailTemplateAttempt3: props.emailTemplateAttemptBundle3,
    bundleAttempt3: props.attemptBundle3,
    emailTemplateAttemptED1: props.emailTemplateAttemptED1,
    emailTemplateAttemptED2: props.emailTemplateAttemptED2,
    emailTemplateAttemptED3: props.emailTemplateAttemptED3,
    attemptED1: props.attemptED1,
    attemptED2: props.attemptED2,
    attemptED3: props.attemptED3,
    ptFeeMethod: props.prFeeMethod,
    clearFeeForClearPackage: Number(props.clearFeeForClearPackage),
    clearTransactionFeeForPR: Number(props.clearTransactionFeeForPR),
    patientResponsibilityDiscount: Number(props.patientResponsibilityDiscount),
    clearFinancingFee: Number(props.clearFinancingFee),
    clearHealthFeeForGFE: Number(props.clearHealthFeeForGFE),
    clearHealthDiscountForGFE: Number(props.clearHealthDiscountForGFE),
    accountLogoUrl: props.accountLogoUrl,
    contactEmail: props.contactEmail,
    expirationDays: Number(props.expirationDays),
    attemptBariatrics1: props.attemptBariatrics1,
    attemptBariatrics2: props.attemptBariatrics2,
    attemptBariatrics3: props.attemptBariatrics3,
    emailTemplateAttemptBariatrics1: props.emailTemplateAttemptBariatrics1,
    emailTemplateAttemptBariatrics2: props.emailTemplateAttemptBariatrics2,
    emailTemplateAttemptBariatrics3: props.emailTemplateAttemptBariatrics3,
    attemptGFE1: props.attemptGFE1,
    attemptGFE2: props.attemptGFE2,
    attemptGFE3: props.attemptGFE3,
    emailTemplateAttemptGFE1: props.emailTemplateAttemptGFE1,
    emailTemplateAttemptGFE2: props.emailTemplateAttemptGFE2,
    emailTemplateAttemptGFE3: props.emailTemplateAttemptGFE3,
    publicContactName: props.publicContactName,
    publicContactRole: props.publicContactRole,
    publicContactPhone: props.publicContactPhone,
    eligibilityEnabled: Boolean(props.eligibilityEnabled),
    npi: props.npiNumber,
    paymentPlanTypeEnabled: {
      patientResponsibility: Boolean(props.patientResponsibilityEnabled),
      ed: Boolean(props.edEnabled),
      bariatrics: Boolean(props.bariatricsEnabled),
      gfe: Boolean(props.gfeEnabled),
      bundled: Boolean(props.bundledEnabled),
    },
    orderTypesVisible: {
      patientResponsibility: Boolean(props.patientResponsibilityVisible),
      ed: Boolean(props.edVisible),
      bariatrics: Boolean(props.bariatricsVisible),
      gfe: Boolean(props.gfeVisible),
      bundled: Boolean(props.bundledVisible),
    },
    paymentPlanDuration: props.paymentPlanDuration,
    paymentPlanMinDownPaymentPercent: props.paymentPlanMinDownPaymentPercent,
    paymentPlanAvailability: props.paymentPlanAvailability,
    paymentPlanCustomSettings: props.paymentPlanCustomSettings,
    providerDistributionLogic: props.providerDistributionLogic,
    costApplicationRulesVisible: {
      facilityRules: Boolean(props.facilityRules),
      nonFacilityRules: Boolean(props.nonFacilityRules),
    },
    athenaIntegrationEnabled: Boolean(props.athenaIntegrationEnabled),
    athenaPracticeId: props.athenaPracticeId,
    athenaDepartmentId: props.athenaDepartmentId,
    directPayEnabled: Boolean(props.directPayEnabled),
    partialPaymentsEnabled: Boolean(props.partialPaymentsEnabled),
    features: props.features
      ? {
          markPaidViaCheck: Boolean(props.features.markPaidViaCheck),
          markPaidViaCash: Boolean(props.features.markPaidViaCash),
          markPaidViaCashUnmanaged: Boolean(
            props.features.markPaidViaCashUnmanaged
          ),
          orderPaymentNotesEnabled: Boolean(
            props.features.orderPaymentNotesEnabled
          ),
          hstImport: Boolean(props.features.hstImport),
          implantSuggestions: Boolean(props.features.implantSuggestions),
          checkoutContinueButtonVisible: Boolean(
            props.features.checkoutContinueButtonVisible
          ),
        }
      : undefined,
    locationEnabled: {
      patientResponsibility: Boolean(props.showLocationsPatientResponsibility),
      ed: Boolean(props.showLocationsEd),
      bariatrics: Boolean(props.showLocationsBariatrics),
      gfe: Boolean(props.showLocationsGfe),
      bundled: Boolean(props.showLocationsBundled),
      directPay: Boolean(props.showLocationsDirectPay),
    },
    accountLocationsToCreate,
    accountLocationsToUpdate,
    accountLocationsToDelete,
  };
};
